import api from "../../../services/api.service"

export const setupWizardModule = {
    namespaced: true,
    state: {
        setupStatus: null,
        loading: false,
        currentStep: 0,
        branchId: null,
        branchData: null,
    },
    getters: {
        getSetupStatus(state) {
            return state.setupStatus;
        },
        isLoading(state) {
            return state.loading;
        },
        getCurrentStep(state) {
            return state.currentStep;
        },
        getBranchId(state) {
            return state.branchId;
        },
        getBranchData(state) {
            return state.branchData;
        },
        getCompletionPercentage(state) {
            if (state.setupStatus == null) return 0;
            return state.setupStatus.completionPercentage || 0;
        },
        getChecklist(state) {
            if (state.setupStatus == null) return [];
            const s = state.setupStatus;
            return [
                {
                    id: 'branchInfo',
                    label: 'Branch Information',
                    description: 'Company name, contact details, address, currency',
                    completed: s.branchInfoComplete,
                    step: 0,
                    icon: 'BuildingIcon'
                },
                {
                    id: 'ownerClient',
                    label: 'Create Owner Client',
                    description: 'Add a client as building owner',
                    completed: s.hasOwnerClients,
                    step: 1,
                    icon: 'UserIcon'
                },
                {
                    id: 'firstBuilding',
                    label: 'First Building',
                    description: 'Create first building with owner (required before Stripe)',
                    completed: (s.totalBuildings || 0) > 0,
                    step: 2,
                    icon: 'HomeIcon'
                },
                {
                    id: 'offerTemplates',
                    label: 'Check-in Agreement',
                    description: 'Agreement guests sign before check-in, per building',
                    completed: s.offerTemplatesConfigured,
                    step: 3,
                    icon: 'FileTextIcon'
                },
                {
                    id: 'externalApi',
                    label: 'Channel Manager',
                    description: 'External API type, Channel ID & Secret',
                    completed: s.externalApiConnected,
                    step: 4,
                    icon: 'LinkIcon'
                },
                {
                    id: 'stripe',
                    label: 'Stripe Payment',
                    description: 'Connect with Stripe (applied to buildings without tokens)',
                    completed: s.stripeConnected || s.stripeConnectConnected,
                    step: 5,
                    icon: 'CreditCardIcon'
                },
                {
                    id: 'twillio',
                    label: 'Twilio SMS & Calls',
                    description: 'Twilio Account SID, Auth Token, Phone & WhatsApp sender',
                    completed: s.twillioConnected,
                    step: 6,
                    icon: 'PhoneIcon'
                },
                {
                    id: 'facebookWhatsapp',
                    label: 'WhatsApp (via Twilio)',
                    description: 'Twilio Account SID, Auth Token & WhatsApp sender',
                    completed: s.facebookWhatsappConnected,
                    step: 6,
                    icon: 'MessageCircleIcon'
                },
                {
                    id: 'buildings',
                    label: 'Active Buildings',
                    description: `${s.activeBuildingsCount || 0} of ${s.totalBuildings || 0} buildings active`,
                    completed: s.hasBuildingsActive,
                    step: 7,
                    icon: 'HomeIcon'
                },
                {
                    id: 'realEstates',
                    label: 'Active Properties',
                    description: `${s.activeRealEstatesCount || 0} of ${s.totalRealEstates || 0} properties active`,
                    completed: s.hasRealEstatesActive,
                    step: 8,
                    icon: 'GridIcon'
                },
                {
                    id: 'linkedProperties',
                    label: 'Properties Linked to Buildings',
                    description: `${s.linkedRealEstatesCount || 0} of ${s.totalRealEstates || 0} linked`,
                    completed: s.realEstatesLinkedToBuildings,
                    step: 8,
                    icon: 'Link2Icon'
                },
                {
                    id: 'review',
                    label: 'Final Review',
                    description: 'Review setup summary and finish',
                    completed: true,
                    step: 9,
                    icon: 'CheckSquareIcon'
                },
            ];
        }
    },
    mutations: {
        setSetupStatus(state, status) {
            state.setupStatus = status;
        },
        setLoading(state, loading) {
            state.loading = loading;
        },
        setCurrentStep(state, step) {
            state.currentStep = step;
        },
        setBranchId(state, id) {
            state.branchId = id;
        },
        setBranchData(state, data) {
            state.branchData = data;
        }
    },
    actions: {
        fetchSetupStatus({commit, dispatch}, branchId) {
            commit('setLoading', true);
            return new Promise((resolve, reject) => {
                api.sendRequest('setup-wizard/status/' + branchId).then(
                    (ans) => {
                        commit('setSetupStatus', ans['SetupStatus']);
                        commit('setLoading', false);
                        resolve(ans['SetupStatus']);
                    },
                    (error) => {
                        commit('setLoading', false);
                        dispatch('notifications/error',
                            {title: 'Setup Wizard', message: error.message || 'Failed to load setup status'},
                            {root: true});
                        reject(error.message);
                    }
                );
            });
        },
        loadBranch({commit, dispatch}, branchId) {
            return new Promise((resolve, reject) => {
                api.sendRequest('branches/id/' + branchId).then(
                    (ans) => {
                        commit('setBranchData', ans['Branch']);
                        resolve(ans['Branch']);
                    },
                    (error) => {
                        dispatch('notifications/error',
                            {title: 'Setup Wizard', message: error.message || 'Failed to load branch'},
                            {root: true});
                        reject(error.message);
                    }
                );
            });
        },
        saveBranch({commit, dispatch}, {id, branch}) {
            return new Promise((resolve, reject) => {
                let body = {...branch};
                body['id'] = id;
                api.sendRequest('branches/update', null, body).then(
                    (ans) => {
                        dispatch('notifications/success',
                            {title: 'Setup Wizard', message: 'Branch updated successfully'},
                            {root: true});
                        resolve(ans);
                    },
                    (error) => {
                        dispatch('notifications/error',
                            {title: 'Setup Wizard', message: error.message || 'Failed to save branch'},
                            {root: true});
                        reject(error.message);
                    }
                );
            });
        },
        setStep({commit}, step) {
            commit('setCurrentStep', step);
        },
        initWizard({commit, dispatch}, branchId) {
            commit('setBranchId', branchId);
            return Promise.all([
                dispatch('fetchSetupStatus', branchId),
                dispatch('loadBranch', branchId)
            ]);
        },
        applyStripeConnectToBuildings({dispatch}, branchId) {
            return new Promise((resolve, reject) => {
                api.sendRequest('setup-wizard/stripe-connect/apply-to-buildings/' + branchId).then(
                    (ans) => {
                        resolve(ans['buildingsUpdated'] || 0);
                    },
                    (error) => {
                        dispatch('notifications/error',
                            {title: 'Stripe Connect', message: error.message || 'Failed to apply to buildings'},
                            {root: true});
                        reject(error.message);
                    }
                );
            });
        },
        createStripeConnectLink({dispatch}, branchId) {
            return new Promise((resolve, reject) => {
                api.sendRequest('setup-wizard/stripe-connect/link/' + branchId).then(
                    (ans) => {
                        if (ans['url']) {
                            resolve(ans['url']);
                        } else {
                            reject('No URL returned');
                        }
                    },
                    (error) => {
                        dispatch('notifications/error',
                            {title: 'Stripe Connect', message: error.message || 'Failed to create link'},
                            {root: true});
                        reject(error.message);
                    }
                );
            });
        },
        createBuildingStripeConnectLink({dispatch}, {branchId, buildingId}) {
            return new Promise((resolve, reject) => {
                api.sendRequest('setup-wizard/stripe-connect/building-link/' + branchId + '/' + buildingId).then(
                    (ans) => {
                        if (ans['url']) {
                            resolve(ans['url']);
                        } else {
                            reject('No URL returned');
                        }
                    },
                    (error) => {
                        dispatch('notifications/error',
                            {title: 'Stripe Connect', message: error.message || 'Failed to create building link'},
                            {root: true});
                        reject(error.message);
                    }
                );
            });
        },
        getWhatsAppAuthUrl({dispatch}, branchId) {
            return new Promise((resolve, reject) => {
                api.sendRequest('setup-wizard/whatsapp/auth-url/' + branchId).then(
                    (ans) => {
                        if (ans['url']) {
                            resolve(ans['url']);
                        } else {
                            reject('No URL returned');
                        }
                    },
                    (error) => {
                        dispatch('notifications/error',
                            {title: 'WhatsApp Connect', message: error.message || 'Failed to get auth URL'},
                            {root: true});
                        reject(error.message);
                    }
                );
            });
        },
        setupTwilio({dispatch}, {branchId, purchaseNumber = true}) {
            return new Promise((resolve, reject) => {
                const qs = purchaseNumber !== undefined ? `?purchaseNumber=${purchaseNumber}` : '';
                api.sendRequest('setup-wizard/twilio/setup/' + branchId + qs).then(
                    (ans) => resolve(ans),
                    (error) => reject(error && (error.message || error) || 'Failed to setup Twilio')
                );
            });
        }
    }
};
